import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import {
  HELP_TEXT,
  ROLE_ASSET_KIND_HELP,
  ROLE_ASSET_SCOPE_HELP,
  ROLE_ASSET_STATUS_HELP,
} from '../../components/claude-code/agentTelemetryHelp';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';
import SkillBundleReviewPanel from '../../components/claude-code/SkillBundleReviewPanel';
import {
  useAgentRoleAssetDetail,
  usePublishAgentRoleAsset,
  useRetireAgentRoleAsset,
  useRollbackAgentRoleAsset,
} from '../../apps/apis/claudeCodeDashboard/hooks';
import { roleVersionReviewAssets } from '../../apps/apis/claudeCodeDashboard/types';
import type {
  AgentRoleAssetMetricsRow,
  AgentRoleAssetRow,
  AgentRoleAssetVersionRow,
} from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import {
  formatCost,
  formatCount,
  formatDate,
  kindLabel,
  metricHealthVariant,
  roleSummary,
  scopeLabel,
  statusVariant,
} from './roleAssetUi';

const AgentRoleAssetDetailPage = () => {
  const params = useParams<{ assetId: string }>();
  const assetId = params.assetId ? decodeURIComponent(params.assetId) : '';

  return (
    <ClaudeCodeLoginGate>
      {(session) => (
        <Container fluid className="py-4">
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
            <div>
              <Link to={ROUTES.agentTelemetry.roleAssets} className="small text-muted">
                Back to Role Asset Library
              </Link>
              <h2 className="mb-1">
                <HelpLabel description="Inspect one shared role asset, its content versions, metrics, source proposal, and governance actions.">
                  Role Asset Detail
                </HelpLabel>
              </h2>
              <div className="text-muted small">
                Signed in as <strong>{session.email}</strong>
                {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
              </div>
            </div>
          </div>
          {session.isAdmin ? (
            <RoleAssetDetailBody assetId={assetId} />
          ) : (
            <Alert variant="warning">Role Asset Library is admin-only.</Alert>
          )}
        </Container>
      )}
    </ClaudeCodeLoginGate>
  );
};

export const previousPersistedRoleAssetVersion = (
  versions: AgentRoleAssetVersionRow[],
  selectedVersion: number,
): AgentRoleAssetVersionRow | null => {
  const selectedIndex = versions.findIndex((version) => version.version === selectedVersion);
  return selectedIndex >= 0 ? (versions[selectedIndex + 1] ?? null) : null;
};

function RoleAssetDetailBody({ assetId }: { assetId: string }) {
  const detail = useAgentRoleAssetDetail(assetId, { enabled: assetId.length > 0 });
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  useEffect(() => {
    if (detail.data?.asset.latestVersion) {
      setSelectedVersion(detail.data.asset.latestVersion);
    }
  }, [detail.data?.asset.latestVersion]);

  if (!assetId) {
    return <Alert variant="danger">Missing role asset id.</Alert>;
  }
  if (detail.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (detail.error) {
    return <Alert variant="danger">Failed to load role asset detail: {(detail.error as Error).message}</Alert>;
  }
  if (!detail.data) {
    return <Alert variant="warning">Role asset detail was not returned.</Alert>;
  }

  const versions = [...detail.data.versions].sort((left, right) => right.version - left.version);
  const selected = versions.find((version) => version.version === selectedVersion) ?? versions[0] ?? null;
  const previous = selected ? previousPersistedRoleAssetVersion(versions, selected.version) : null;
  const selectedReviewAssets = selected ? roleVersionReviewAssets(selected) : [];
  const previousReviewAssets = previous ? roleVersionReviewAssets(previous) : [];

  return (
    <Row className="g-3">
      <Col xl={4}>
        <RoleAssetSummaryCard asset={detail.data.asset} />
        <RoleAssetGovernanceCard asset={detail.data.asset} versions={versions} refetchDetail={() => detail.refetch()} />
        <SourceCard asset={detail.data.asset} />
        <MetricsCard asset={detail.data.asset} />
      </Col>
      <Col xl={8}>
        <SkillBundleReviewPanel
          assets={selectedReviewAssets}
          previousAssets={previousReviewAssets}
          title="Role asset source review"
        />
        <VersionHistoryCard
          versions={versions}
          selectedVersion={selected?.version ?? null}
          setSelectedVersion={setSelectedVersion}
        />
      </Col>
    </Row>
  );
}

function RoleAssetSummaryCard({ asset }: { asset: AgentRoleAssetRow }) {
  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between gap-2 align-items-start mb-2">
          <div>
            <h4 className="mb-1">{asset.title}</h4>
            <div className="text-muted small">{asset.assetId}</div>
          </div>
          <HelpBadge bg={statusVariant(asset.status)} description={ROLE_ASSET_STATUS_HELP[asset.status]}>
            {asset.status}
          </HelpBadge>
        </div>
        <p>{asset.description}</p>
        <Row className="g-2 small">
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.assetKind}>Kind</HelpLabel>
            </div>
            <div>
              <HelpLabel description={ROLE_ASSET_KIND_HELP[asset.kind]}>{kindLabel(asset.kind)}</HelpLabel>
            </div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Runtime adapter where this asset is installed.">Adapter</HelpLabel>
            </div>
            <div>{asset.adapter}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.assetScope}>Scope</HelpLabel>
            </div>
            <div>
              <HelpLabel description={ROLE_ASSET_SCOPE_HELP[asset.scope]}>{scopeLabel(asset.scope)}</HelpLabel>
            </div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={HELP_TEXT.assetTargeting}>Targeting</HelpLabel>
            </div>
            <div>{roleSummary(asset)}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Version currently distributed by the plugin.">Active version</HelpLabel>
            </div>
            <div>{asset.activeVersion ?? '-'}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Newest version stored in the role asset registry.">Latest version</HelpLabel>
            </div>
            <div>{asset.latestVersion}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Employee who created or owns this shared asset.">Owner</HelpLabel>
            </div>
            <div>{asset.ownerEmployeeId}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Employee who last reviewed this shared asset.">Reviewer</HelpLabel>
            </div>
            <div>{asset.reviewerEmployeeId ?? '-'}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Last time this asset record changed.">Updated</HelpLabel>
            </div>
            <div>{formatDate(asset.updatedAt)}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="When the current active version was published.">Published</HelpLabel>
            </div>
            <div>{formatDate(asset.publishedAt)}</div>
          </Col>
        </Row>
        {asset.reviewNote ? (
          <div className="p-3 rounded border bg-light mt-3">
            <div className="text-muted small mb-1">
              <HelpLabel description={HELP_TEXT.reviewNote}>Review note</HelpLabel>
            </div>
            <div className="small">{asset.reviewNote}</div>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

function SourceCard({ asset }: { asset: AgentRoleAssetRow }) {
  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="h6">
          <HelpLabel description={HELP_TEXT.source}>Source</HelpLabel>
        </Card.Title>
        <Row className="g-2 small">
          <Col md={12}>
            <div className="text-muted">
              <HelpLabel description="Skill proposal that produced this shared role asset, when available.">
                Proposal
              </HelpLabel>
            </div>
            <div>
              {asset.sourceProposalId ? (
                <Link to={ROUTES.agentTelemetry.proposalDetail(asset.sourceProposalId)}>{asset.sourceProposalId}</Link>
              ) : (
                '-'
              )}
            </div>
          </Col>
          <Col md={12}>
            <div className="text-muted">
              <HelpLabel description="Generated proposal asset id that was converted into this shared asset.">
                Proposal asset
              </HelpLabel>
            </div>
            <div>{asset.sourceProposalAssetId ?? '-'}</div>
          </Col>
          <Col md={12}>
            <div className="text-muted">
              <HelpLabel description="Hash used to detect content changes across generated asset versions.">
                Content hash
              </HelpLabel>
            </div>
            <div className="text-break">{asset.contentHash ?? '-'}</div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

interface RoleAssetGovernanceCardProps {
  asset: AgentRoleAssetRow;
  versions: AgentRoleAssetVersionRow[];
  refetchDetail: () => Promise<unknown>;
}

function RoleAssetGovernanceCard({ asset, versions, refetchDetail }: RoleAssetGovernanceCardProps) {
  const publish = usePublishAgentRoleAsset();
  const retire = useRetireAgentRoleAsset();
  const rollback = useRollbackAgentRoleAsset();
  const rollbackCandidates = versions
    .filter((version) => version.status !== 'draft')
    .sort((left, right) => right.version - left.version);
  const [note, setNote] = useState('');
  const [rollbackVersion, setRollbackVersion] = useState<number | ''>(rollbackCandidates[0]?.version ?? '');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (rollbackVersion === '' && rollbackCandidates[0]) {
      setRollbackVersion(rollbackCandidates[0].version);
    }
  }, [rollbackCandidates, rollbackVersion]);

  const isSubmitting = publish.isPending || retire.isPending || rollback.isPending;
  const trimmedNote = note.trim();
  const canPublish = asset.status === 'draft';
  const canRetire = asset.status === 'active';
  const canRollback = asset.status !== 'retired' && rollbackVersion !== '';

  const runAction = async (action: 'publish' | 'retire' | 'rollback') => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      const noteParam = trimmedNote ? { note: trimmedNote } : {};
      if (action === 'publish') {
        await publish.mutateAsync({ assetId: asset.assetId, ...noteParam });
        setSuccessMessage('Role asset published. Detail refreshed.');
      } else if (action === 'retire') {
        await retire.mutateAsync({ assetId: asset.assetId, ...noteParam });
        setSuccessMessage('Role asset retired. Detail refreshed.');
      } else {
        if (rollbackVersion === '') {
          setErrorMessage('Select a published or retired version before rollback.');
          return;
        }
        await rollback.mutateAsync({
          assetId: asset.assetId,
          version: rollbackVersion,
          ...noteParam,
        });
        setSuccessMessage(`Rollback draft created from version ${rollbackVersion}. Detail refreshed.`);
      }
      setNote('');
      await refetchDetail();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Role asset lifecycle action failed.');
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="h6">
          <HelpLabel description={HELP_TEXT.governanceActions}>Governance actions</HelpLabel>
        </Card.Title>
        <Alert variant="light" className="border small">
          Actions are append-only in the role asset registry. Add a note that explains why this state transition is
          safe.
        </Alert>
        {successMessage ? <Alert variant="success">{successMessage}</Alert> : null}
        {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
        <Form.Group className="mb-3">
          <Form.Label className="small text-muted">
            <HelpLabel description={HELP_TEXT.reviewNote}>Review note</HelpLabel>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Why are we publishing, retiring, or rolling back this asset?"
            disabled={isSubmitting}
          />
        </Form.Group>
        <div className="d-flex gap-2 flex-wrap mb-3">
          <Button variant="success" disabled={!canPublish || isSubmitting} onClick={() => void runAction('publish')}>
            Publish latest draft
          </Button>
          <Button
            variant="outline-danger"
            disabled={!canRetire || isSubmitting}
            onClick={() => void runAction('retire')}
          >
            Retire active asset
          </Button>
        </div>
        <Form.Group className="mb-2">
          <Form.Label className="small text-muted">
            <HelpLabel description="Published or retired version used as the source for a new rollback draft.">
              Rollback source version
            </HelpLabel>
          </Form.Label>
          <Form.Select
            value={rollbackVersion}
            disabled={rollbackCandidates.length === 0 || isSubmitting}
            onChange={(event) => setRollbackVersion(Number(event.target.value))}
          >
            {rollbackCandidates.length ? (
              rollbackCandidates.map((version) => (
                <option key={version.version} value={version.version}>
                  Version {version.version} · {version.status} · {formatDate(version.updatedAt)}
                </option>
              ))
            ) : (
              <option value="">No published or retired versions</option>
            )}
          </Form.Select>
        </Form.Group>
        <Button
          variant="outline-primary"
          disabled={!canRollback || isSubmitting}
          onClick={() => void runAction('rollback')}
        >
          Create rollback draft
        </Button>
        <div className="text-muted small mt-2">
          Publish requires a draft asset. Retire requires an active asset. Rollback creates a new draft from a
          previously published or retired version.
        </div>
      </Card.Body>
    </Card>
  );
}

function MetricsCard({ asset }: { asset: AgentRoleAssetRow }) {
  const metrics = asset.metrics;
  const adoptionLabel = `${formatCount(metrics.appliedEmployeeCount)} / ${formatCount(metrics.eligibleEmployeeCount)}`;
  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
          <div>
            <Card.Title className="h6 mb-1">
              <HelpLabel description={HELP_TEXT.usageMetrics}>Usage metrics</HelpLabel>
            </Card.Title>
            <div className="text-muted small">
              Last {metrics.windowDays} days · {formatDate(metrics.rangeStart)} to {formatDate(metrics.rangeEnd)}
            </div>
          </div>
          <HelpBadge
            bg={metricHealthVariant(asset)}
            description="Quick health signal from failures and observed usage."
          >
            {asset.metrics.failedCount > 0 ? 'Needs review' : asset.metrics.usageCount > 0 ? 'Used' : 'Watch'}
          </HelpBadge>
        </div>
        <Row className="g-2">
          <MetricTile
            label="Eligible / applied employees"
            description={HELP_TEXT.adoption}
            value={adoptionLabel}
            detail={`${formatCount(metrics.appliedCount)} apply events`}
          />
          <MetricTile
            label="Runtime usage"
            description={HELP_TEXT.usage}
            value={formatCount(metrics.usageCount)}
            detail={`${formatCount(metrics.impactedSessionCount)} impacted sessions`}
          />
          <MetricTile
            label="Failures"
            description={HELP_TEXT.failures}
            value={formatCount(metrics.failedCount)}
            detail={`${formatCount(metrics.skippedCount)} skipped deliveries`}
          />
          <MetricTile
            label="Tokens"
            description="Input/output token totals from sessions associated with this asset."
            value={`${formatCount(metrics.impactedInputTokens)} / ${formatCount(metrics.impactedOutputTokens)}`}
            detail="input / output"
          />
          <MetricTile
            label="Cost impact"
            description="Estimated USD cost from sessions associated with this asset."
            value={formatCost(metrics.impactedCostUsd)}
            detail="session-level estimate"
          />
          <MetricTile
            label="Last activity"
            description="Most recent delivery or runtime usage timestamp for this asset."
            value={formatDate(metrics.lastUsedAt ?? metrics.lastAppliedAt)}
            detail={lastActivityDetail(metrics)}
          />
        </Row>
        <Alert variant="light" className="border small mt-3 mb-0">
          Usage and cost are estimated from sessions that invoked the inferred skill or agent name. Delivery counts are
          direct `SharedRoleAsset*` events from the plugin.
        </Alert>
      </Card.Body>
    </Card>
  );
}

function MetricTile({
  label,
  description,
  value,
  detail,
}: {
  label: string;
  description: string;
  value: string;
  detail: string;
}) {
  return (
    <Col md={6}>
      <div className="border rounded p-2 h-100">
        <div className="text-muted small">
          <HelpLabel description={description}>{label}</HelpLabel>
        </div>
        <div className="fw-semibold">{value}</div>
        <div className="text-muted small">{detail}</div>
      </div>
    </Col>
  );
}

function lastActivityDetail(metrics: AgentRoleAssetMetricsRow) {
  if (metrics.lastUsedAt) {
    return 'last runtime usage';
  }
  if (metrics.lastAppliedAt) {
    return 'last delivery';
  }
  return 'no activity yet';
}

interface VersionHistoryCardProps {
  versions: AgentRoleAssetVersionRow[];
  selectedVersion: number | null;
  setSelectedVersion: (version: number) => void;
}

function VersionHistoryCard({ versions, selectedVersion, setSelectedVersion }: VersionHistoryCardProps) {
  return (
    <Card>
      <Card.Header className="fw-semibold">
        <HelpLabel description={HELP_TEXT.versionHistory}>Version history</HelpLabel>
      </Card.Header>
      <ListGroup variant="flush">
        {versions.length ? (
          versions.map((version) => (
            <ListGroup.Item
              key={`${version.assetId}-${version.version}`}
              action
              active={version.version === selectedVersion}
              onClick={() => setSelectedVersion(version.version)}
            >
              <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                <div>
                  <div className="fw-semibold">
                    Version {version.version} · {version.title}
                  </div>
                  <div className="small">
                    {version.targetPath} · {version.createdByEmployeeId}
                  </div>
                </div>
                <div className="d-flex gap-1 flex-wrap">
                  <HelpBadge bg={statusVariant(version.status)} description={ROLE_ASSET_STATUS_HELP[version.status]}>
                    {version.status}
                  </HelpBadge>
                  <HelpBadge bg="secondary" description="Last update timestamp for this version.">
                    {formatDate(version.updatedAt)}
                  </HelpBadge>
                </div>
              </div>
              {version.reviewNote ? <div className="small mt-2">{version.reviewNote}</div> : null}
            </ListGroup.Item>
          ))
        ) : (
          <ListGroup.Item className="text-muted">No versions are available.</ListGroup.Item>
        )}
      </ListGroup>
    </Card>
  );
}

export default AgentRoleAssetDetailPage;
